import React from 'react';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import theme, { cssVariables } from './styledTheme';

const GlobalStyles = createGlobalStyle`
  ${cssVariables}
  
  * {
    box-sizing: border-box;
  }
  
  body {
    font-family: var(--font-sans);
    background: var(--color-background-body);
    /* Uncomment for gradient background:
    background: var(--color-background-gradient);
    */
    /* Uncomment for pattern background:
    background: var(--color-background-pattern);
    background-size: 100px 100px;
    */
    color: var(--color-text-primary);
    line-height: var(--leading-normal);
    margin: 0;
    padding: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    min-height: 100vh;
  }
  
  /* Button Base Styles */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--spacing-sm);
    font-family: var(--font-sans);
    font-weight: var(--font-medium);
    font-size: var(--text-base);
    line-height: 1;
    text-decoration: none;
    border: none;
    border-radius: var(--border-radius-lg);
    padding: var(--spacing-sm) var(--spacing-lg);
    cursor: pointer;
    transition: all var(--transition-normal);
    user-select: none;
    
    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(167, 199, 231, 0.3);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
  
  .btn-primary {
    background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
    color: var(--color-text-inverse);
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary) 100%);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  .btn-outline {
    background: var(--color-background-paper);
    color: var(--color-primary);
    border: 2px solid var(--color-primary);
    
    &:hover:not(:disabled) {
      background: var(--color-primary-light);
      color: var(--color-text-inverse);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  .btn-secondary {
    background: var(--color-secondary);
    color: var(--color-text-primary);
    
    &:hover:not(:disabled) {
      background: var(--color-secondary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }
  
  .btn-ghost {
    background: transparent;
    color: var(--color-text-secondary);
    
    &:hover:not(:disabled) {
      background: var(--color-gray-100);
      color: var(--color-text-primary);
    }
  }
  
  /* Form Elements */
  .form-input {
    width: 100%;
    padding: var(--spacing-sm) var(--spacing-md);
    border: 2px solid var(--color-border-light);
    border-radius: var(--border-radius-md);
    font-family: var(--font-sans);
    font-size: var(--text-base);
    background: var(--color-background-paper);
    color: var(--color-text-primary);
    transition: border-color var(--transition-normal);
    
    &:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(167, 199, 231, 0.1);
    }
    
    &::placeholder {
      color: var(--color-text-muted);
    }
  }
  
  .form-select {
    width: 100%;
    padding: var(--spacing-sm) var(--spacing-md);
    border: 2px solid var(--color-border-light);
    border-radius: var(--border-radius-md);
    font-family: var(--font-sans);
    font-size: var(--text-base);
    background: var(--color-background-paper);
    color: var(--color-text-primary);
    cursor: pointer;
    transition: border-color var(--transition-normal);
    
    &:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 3px rgba(167, 199, 231, 0.1);
    }
  }
  
  /* Card Styles */
  .card {
    background: var(--color-background-paper);
    border-radius: var(--border-radius-xl);
    box-shadow: var(--shadow-lg);
    border: 1px solid var(--color-border-light);
    padding: var(--spacing-lg);
    transition: all var(--transition-normal);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-xl);
      border-color: var(--color-primary);
    }
  }
  
  /* Text Utilities */
  .text-primary { color: var(--color-text-primary); }
  .text-secondary { color: var(--color-text-secondary); }
  .text-muted { color: var(--color-text-muted); }
  .text-success { color: var(--color-success); }
  .text-warning { color: var(--color-warning); }
  .text-error { color: var(--color-error); }
  .text-info { color: var(--color-info); }
  
  /* Font Utilities */
  .font-sans { font-family: var(--font-sans); }
  .font-serif { font-family: var(--font-serif); }
  .font-mono { font-family: var(--font-mono); }
  
  /* Background Utilities */
  .bg-primary { background-color: var(--color-primary); }
  .bg-secondary { background-color: var(--color-secondary); }
  .bg-light { background-color: var(--color-background-light); }
  .bg-paper { background-color: var(--color-background-paper); }
`;

interface GlobalThemeProviderProps {
  children: React.ReactNode;
}

export const GlobalThemeProvider: React.FC<GlobalThemeProviderProps> = ({ children }) => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      {children}
    </ThemeProvider>
  );
};

export default GlobalThemeProvider;